import React, { useMemo } from "react";
import { Empty, Pagination, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import * as TYPES from "@src/redux/types";

interface ReportTableProps {
  result: TYPES.ReportListResponse | null;
  loading: boolean;
  onPageChange: (pageNumber: number, pageLimit: number) => void;
}

type ReportTableRow = {
  key: string;
  valueList: string[];
};

const ReportTable: React.FC<ReportTableProps> = ({
  result,
  loading,
  onPageChange,
}) => {
  const { t } = useTranslation();

  const columnList = result?.columnList ?? [];

  // Columns are positional: `valueList[i]` belongs to `columnList[i]`, which
  // is the same alignment the exported sheet uses.
  const columns: ColumnsType<ReportTableRow> = useMemo(
    () =>
      columnList.map((column, index) => ({
        title:
          column.type === "question" ? (
            <Tooltip title={column.label}>
              <span className="report-table-question-header">
                {column.label}
              </span>
            </Tooltip>
          ) : (
            column.label
          ),
        dataIndex: ["valueList", index],
        key: `${column.key}-${index}`,
        width: column.type === "serial" ? 70 : 160,
        ellipsis: true,
        render: (_: unknown, row: ReportTableRow) => row.valueList?.[index] ?? "",
      })),
    [columnList]
  );

  const dataSource: ReportTableRow[] = useMemo(
    () =>
      (result?.items ?? []).map((item, index) => ({
        key: item.readingId ?? String(index),
        valueList: item.valueList ?? [],
      })),
    [result]
  );

  const pageNumber = result?.pagination?.pageNumber ?? 1;
  const pageLimit = result?.pagination?.pageLimit ?? 10;
  const totalCount = result?.pagination?.totalCount ?? 0;

  return (
    <div className="report-table-container">
      <Table<ReportTableRow>
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={false}
        scroll={{ x: "max-content" }}
        size="small"
        bordered
        locale={{
          emptyText: (
            <Empty description={t("no_readings_found") || "No readings found"} />
          ),
        }}
      />
      {totalCount > 0 && (
        <div className="report-table-pagination">
          <Pagination
            current={pageNumber}
            pageSize={pageLimit}
            total={totalCount}
            onChange={onPageChange}
            showSizeChanger
            pageSizeOptions={[10, 20, 50, 100]}
            showTotal={(total) =>
              `${total} ${t("readings") || "readings"}`
            }
          />
        </div>
      )}
    </div>
  );
};

export default ReportTable;
